// UserRolesService.ts
import { ApiResponse } from "../../interfaces/genericResponse/GenericResponse";
import Role from "../../interfaces/identity/Role";
import { UserWithRoleDto } from "../../interfaces/identity/Team";
import { RegionDto, UserDto, UserResponse } from "../../interfaces/team/RegionHierarchy";
import { TeamUser } from "../../interfaces/team/TeamUser";

// Function to fetch a user's role by their user id
export async function getTeamBySupervisor(
    authToken: string
): Promise<TeamUser[] | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/users/supervisorTeam`;

    try {
        const response = await fetch(apiUrl, {
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            // Handle non-OK responses, e.g., 404 for not found
            return null;
        }

        const userData: TeamUser[] = await response.json();
        // Map the role data to Role interface

        return userData;
    } catch (error) {
        // Handle network errors or other exceptions
        console.error("Error fetching task:", error);
        return null;
    }
}

export async function getRegionHierarchy(
    authToken: string,
    regionId: number
): Promise<RegionDto | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/users/organization/${regionId}`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch region hierarchy:", response.statusText);
            return null;
        }

        const data: RegionDto = await response.json();
        return data;
    } catch (err) {
        console.error("Error fetching region hierarchy:", err);
        return null;
    }
}

export async function getAllUsers(
    authToken: string,
    requestBody: {
        searchTerm?: string | null;
        sortBy?: string | null;
        sortDescending?: boolean;
        pageNumber?: number;
        pageSize?: number;
    } = {}
): Promise<{ data: UserDto[]; totalCount: number } | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/Authentication/GetAllUsers`;

    try {
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                searchTerm: null,
                sortBy: null,
                sortDescending: true,
                pageNumber: 1,
                pageSize: 10,
                ...requestBody,
            }),
        });

        if (!response.ok) {
            console.error("Failed to fetch users:", response.statusText);
            return null;
        }

        const json = await response.json();
        const users: UserDto[] = json?.data?.data || [];
        const totalCount: number = json?.data?.totalRecords ?? 0;

        return {
            data: users,
            totalCount,
        };
    } catch (err) {
        console.error("Error fetching users:", err);
        return null;
    }
}


export async function getAllUsersWithSameOrganizationandRegion(
    authToken: string,
    requestBody: {
        searchTerm?: string | null;
        sortBy?: string | null;
        sortDescending?: boolean;
        pageNumber?: number;
        pageSize?: number;
    } = {}
): Promise<{ data: UserDto[]; totalCount: number } | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/Authentication/GetAllUsers/same-region-org`;

    try {
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                searchTerm: null,
                sortBy: null,
                sortDescending: true,
                pageNumber: 1,
                pageSize: 10,
                ...requestBody,
            }),
        });

        if (!response.ok) {
            console.error("Failed to fetch users:", response.statusText);
            return null;
        }

        const json = await response.json();
        const users: UserDto[] = json?.data?.data || [];
        const totalCount: number = json?.data?.totalRecords ?? 0;

        return {
            data: users,
            totalCount,
        };
    } catch (err) {
        console.error("Error fetching users:", err);
        return null;
    }
}


export async function getAllUsersWithSameOrganizationandRegionForSharingFile(
    authToken: string,
    requestBody: {
        searchTerm?: string | null;
        sortBy?: string | null;
        sortDescending?: boolean;
        pageNumber?: number;
        pageSize?: number;        
        fileId?: string;
    } = {}
): Promise<{ data: UserDto[]; totalCount: number } | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/Authentication/GetAllUsers/same-region-org/sharingfile`;

    try {
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                searchTerm: null,
                sortBy: null,
                sortDescending: true,
                pageNumber: 1,
                pageSize: 10,
                ...requestBody,
            }),
        });

        if (!response.ok) {
            console.error("Failed to fetch users:", response.statusText);
            return null;
        }

        const json = await response.json();
        const users: UserDto[] = json?.data?.data || [];
        const totalCount: number = json?.data?.totalRecords ?? 0;

        return {
            data: users,
            totalCount,
        };
    } catch (err) {
        console.error("Error fetching users:", err);
        return null;
    }
}


export async function updateUserStatus(
    userId: string,
    enable: boolean,
    authToken: string
): Promise<boolean> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/Authentication/toggle-status`;

    try {
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ userId, enable }),
        });

        return response.ok;
    } catch (err) {
        console.error("Error updating user status:", err);
        return false;
    }
}

export async function getAllRoles(
    authToken: string
): Promise<Role[] | null> {
    const apiUrl: string = `${process.env.REACT_APP_API_BASE_URL}/users/allRoles`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch roles:", response.statusText);
            return null;
        }

        const data: Role[] = await response.json();

        return data;
    } catch (err) {
        console.error("Error fetching roles:", err);
        return null;
    }
}

export async function createUser(
    formData: FormData,
    authToken: string
): Promise<ApiResponse<string>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/Authentication/register`;

    console.log('--- Inspecting FormData before sending ---');
    // Convert iterator to an array first
    for (let pair of Array.from(formData.entries())) { // <-- Changed here
        // pair is now a [string, FormDataEntryValue] tuple (array)
        const key = pair[0];
        const value = pair[1];

        if (value instanceof File) {
            console.log(`  ${key}: [File: name=${value.name}, size=${value.size}, type=${value.type}]`);
        } else {
            console.log(`  ${key}: ${value}`);
        }
    }
    console.log('--- End FormData Inspection ---');
    try {
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
            body: formData,
        });
        console.log('API Response:', response); // Log the entire response object

        return await response.json();
    } catch (err) {
        console.error("Error during API request:", err);
        throw err;
    }
}

export async function uploadUsersFromCsv(
    csvFile: File,
    authToken: string
): Promise<ApiResponse<any>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/Authentication/register-bulk`;
    
    const formData = new FormData();
    formData.append("csvFile", csvFile);
    
    try {
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
            },
            body: formData,
        });
        
        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || `Upload failed with status ${response.status}`);
        }
        
        return await response.json();
    } catch (err: any) {
        console.error("Error during CSV upload:", err);
        throw err;
    }
}

export async function getUserById(
    userId: string,
    authToken: string
): Promise<UserResponse | null> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/Authentication/GetUserDetailsByUserID/${userId}`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            console.error("Failed to fetch user:", response.statusText);
            return null;
        }

        const data: UserResponse = await response.json();
        return data;
    } catch (err) {
        console.error("Error fetching user:", err);
        return null;
    }
}


export async function updateUser(
    formData: {
        firstName?: string;
        lastName?: string;
        displayName?: string;
        email?: string;
        phoneNumber?: string;
        scId?: string;
        roleIds?: number[];
        regionIds?: number[];
        ManagerId?: string;
        teamIds?: number[];
        timeZone?: string;
        waiverFamilyIds?: number[];
    },
    userId: string,
    authToken: string
): Promise<ApiResponse<UserWithRoleDto>> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/Authentication/UpdateUser/${userId}`;

    try {
        const response = await fetch(apiUrl, {
            method: "PUT",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
        });

        return await response.json();
    } catch (err) {
        console.error("Error updating user status:", err);
        throw err;
    }
}

export async function changePassword(
    newPassword: string,
    authToken: string
): Promise<boolean> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/Authentication/change-password`;

    try {
        const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                idToken: authToken,
                newPassword,
            }),
        });

        return response.ok;
    } catch (err) {
        console.error("Error updating password:", err);
        return false;
    }
}

export async function getExistUser(
    email: string,
    authToken: string
): Promise<boolean> {
    const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/Authentication/CheckEmailExists?email=${email}`;

    try {
        const response = await fetch(apiUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (response.status === 200) {
            const data = await response.json();
            console.log(data, "Email check response");
            return data.data === true;
        }

        if (response.status === 404) {
            return false; // email does NOT exist
        }

        return false; // fallback for any other case
    } catch (error) {
        console.error("Error checking email existence:", error);
        return false;
    }
}

